// src/components/AboutUs/AboutUs.js
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Divider } from 'antd';
import { 
  RocketFilled, 
  EyeFilled,
  TeamOutlined,
  TruckFilled,
  SafetyCertificateFilled,
  GlobalOutlined,
  ClockCircleFilled,
  CheckCircleFilled
} from '@ant-design/icons';
import './AboutUs.css';

const { Title, Paragraph } = Typography;

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('about-us');
      if (section) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          setIsVisible(true);
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const stats = [
    { icon: <TruckFilled />, value: '150+', label: 'Fleet Vehicles' },
    { icon: <SafetyCertificateFilled />, value: '99.7%', label: 'Safety Record' },
    { icon: <ClockCircleFilled />, value: '24/7', label: 'Operation Hours' },
    { icon: <TeamOutlined />, value: '50+', label: 'Expert Team Members' }
  ];

  return (
    <section id="about-us" className="about-us-section">
      <div className="container">
        {/* Header */}
        <div className={`section-header ${isVisible ? 'visible' : ''}`}>
          <Title level={1} className="main-title">
            About <span className="title-accent">Us</span>
          </Title>
          <Paragraph className="section-subtitle">
            Delivering excellence in transportation and logistics with innovation, reliability, and commitment to customer satisfaction.
          </Paragraph>
        </div>

        {/* Company Overview */}
        <Row gutter={[48, 32]} className="company-overview" align="middle">
          <Col xs={24} lg={12}>
            <div className={`content-block ${isVisible ? 'visible' : ''}`}>
              <Title level={2} className="section-title">
                Our <span className="title-underline">Story</span>
              </Title>
              <Paragraph className="about-description">
                Founded with a vision to revolutionize the transportation industry, NexusFlow has grown from 
                a modest startup to a leading logistics provider. Our journey began with a single truck and 
                an unwavering commitment to excellence.
              </Paragraph>
              <Paragraph className="about-description">
                Today, we operate a modern fleet equipped with cutting-edge technology, serving clients 
                across various sectors. Our success is built on the pillars of reliability, innovation, 
                and exceptional customer service.
              </Paragraph>
              <Paragraph className="about-description">
                We continuously invest in our people, technology, and processes to ensure we remain at 
                the forefront of the transportation industry, delivering value to our clients and 
                communities we serve.
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <div className={`image-container ${isVisible ? 'visible' : ''}`}>
              <img 
                src="/about-company.jpg" 
                alt="NexusFlow Company"
                className="about-image"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
                }}
              />
              <div className="image-overlay"></div>
            </div>
          </Col>
        </Row>

        <Divider className="section-divider" />

        {/* Vision & Mission */}
        <div className={`vision-mission-section ${isVisible ? 'visible' : ''}`}>
          <Row gutter={[48, 32]}>
            <Col xs={24} md={12}>
              <Card className="vm-card vision-card" hoverable>
                <div className="vm-icon">
                  <EyeFilled />
                </div>
                <Title level={3} className="vm-title">Our Vision</Title>
                <Paragraph className="vm-content">
                  To be the most trusted and innovative transportation partner globally, 
                  setting new standards in logistics excellence and sustainable operations 
                  while creating lasting value for our stakeholders.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="vm-card mission-card" hoverable>
                <div className="vm-icon">
                  <RocketFilled />
                </div>
                <Title level={3} className="vm-title">Our Mission</Title>
                <Paragraph className="vm-content">
                  To provide reliable, efficient, and sustainable transportation solutions 
                  that exceed customer expectations through innovative technology, 
                  exceptional service, and a commitment to safety and environmental responsibility.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>


        <Divider className="section-divider" />

        {/* Core Values */}
        <div className={`values-section ${isVisible ? 'visible' : ''}`}>
          <Title level={2} className="section-title text-center">
            Our Core <span className="title-underline">Values</span>
          </Title>
          <Row gutter={[32, 32]} className="values-grid">
            <Col xs={24} md={8}>
              <div className="value-item">
                <div className="value-icon">
                  <SafetyCertificateFilled />
                </div>
                <Title level={4} className="value-title">Safety First</Title>
                <Paragraph className="value-description">
                  We prioritize the safety of our team, clients, and communities in every operation, 
                  maintaining the highest standards of security and compliance.
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="value-item">
                <div className="value-icon">
                  <CheckCircleFilled />
                </div>
                <Title level={4} className="value-title">Reliability</Title>
                <Paragraph className="value-description">
                  We deliver on our promises with consistent, dependable service that our clients 
                  can trust for their critical transportation needs.
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="value-item">
                <div className="value-icon">
                  <GlobalOutlined />
                </div>
                <Title level={4} className="value-title">Innovation</Title>
                <Paragraph className="value-description">
                  We embrace technology and innovative solutions to continuously improve our services 
                  and stay ahead in the evolving logistics landscape.
                </Paragraph>
              </div>
            </Col>
          </Row>
        </div>

        {/* Call to Action */}
        <div className={`cta-section ${isVisible ? 'visible' : ''}`}>
          <Card className="cta-card">
            <Title level={3} className="cta-title">
              Ready to Experience Excellence in Transportation?
            </Title>
            <Paragraph className="cta-description">
              Join our growing list of satisfied clients and discover how NexusFlow can transform 
              your logistics operations with our premium transportation services.
            </Paragraph>
            <button className="cta-button">
              Get Started Today
            </button>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;